// Importer les heures écrites sur le groupe WhatsApp.
//
// On colle l'export de la conversation, on regarde ce qui a été compris, on
// corrige, on enregistre. Rien n'entre en base sans être passé sous les yeux
// de quelqu'un : la lecture d'un message écrit à la main est une proposition,
// pas une vérité.
//
// Toute l'intelligence de lecture vit dans @imp/shared/releve-heures-whatsapp,
// testée contre les vrais messages du groupe. Ici, on montre et on enregistre.

import { messageErreur } from '@imp/shared/message-erreur';
import { formatMinutes } from '@imp/shared/releve-heures';
import {
  type CodeAnomalie,
  lireHeuresWhatsApp,
  type LigneImportee,
} from '@imp/shared/releve-heures-whatsapp';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { creerReleves, type EmployeHeures, setAliasEmploye, versSalaries } from '@/modules/rh/api';

const LIBELLE_ANOMALIE: Record<CodeAnomalie, string> = {
  horaire_illisible: 'Horaire illisible',
  nom_inconnu: 'Nom non identifié',
  nom_ambigu: 'Nom ambigu',
  date_ecrite_differente: 'Date annoncée différente',
  pause_sans_duree: 'Pause sans durée',
  doublon_jour: 'Doublon',
};

function moisCourant(): string {
  return new Date().toISOString().slice(0, 7);
}

function bornesDuMois(ym: string): { du: string; au: string } {
  const [yStr, mStr] = ym.split('-');
  const dernier = new Date(Number(yStr), Number(mStr), 0).getDate();
  return { du: `${ym}-01`, au: `${ym}-${String(dernier).padStart(2, '0')}` };
}

export function ImportWhatsAppPanel({
  employes,
  onImporte,
}: {
  employes: readonly EmployeHeures[];
  onImporte: () => void;
}) {
  const qc = useQueryClient();
  const bornes = bornesDuMois(moisCourant());
  const [texte, setTexte] = useState('');
  const [depuis, setDepuis] = useState(bornes.du);
  const [jusqua, setJusqua] = useState(bornes.au);
  const [equipes, setEquipes] = useState<Record<string, string>>({});
  const [exclues, setExclues] = useState<Set<number>>(new Set());
  const [datesEcrites, setDatesEcrites] = useState<Set<number>>(new Set());
  const [aliasPour, setAliasPour] = useState<Record<string, string>>({});

  const salaries = useMemo(() => versSalaries(employes), [employes]);

  const resultat = useMemo(
    () =>
      lireHeuresWhatsApp(texte, {
        salaries,
        equipeParAuteur: equipes,
        depuis,
        jusqua,
      }),
    [texte, salaries, equipes, depuis, jusqua],
  );

  const jourRetenu = (l: LigneImportee, i: number): string =>
    datesEcrites.has(i) && l.jourEcrit !== null ? l.jourEcrit : l.jour;

  const retenues = resultat.lignes.filter((_, i) => !exclues.has(i));

  const ajouterAlias = useMutation({
    mutationFn: ({ employeId, alias }: { employeId: string; alias: string }) => {
      const e = employes.find((x) => x.id === employeId);
      return setAliasEmploye(employeId, [...(e?.aliasHeures ?? []), alias]);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employes-heures'] }),
  });

  const enregistrer = useMutation({
    mutationFn: () =>
      creerReleves(
        resultat.lignes
          .map((l, i) => ({ l, i }))
          .filter(({ i }) => !exclues.has(i))
          .map(({ l, i }) => ({
            jour: jourRetenu(l, i),
            employeId: l.employeId,
            nom: l.nom,
            equipe: l.equipe,
            chantierId: null,
            chantierLibelle: l.chantierLibelle,
            debut: l.debut,
            fin: l.fin,
            pauseDeclaree: l.pauseDeclaree,
            note: null,
            source: 'whatsapp' as const,
            sourceTexte: l.source,
          })),
      ),
    onSuccess: () => {
      setTexte('');
      setExclues(new Set());
      setDatesEcrites(new Set());
      onImporte();
    },
  });

  const basculer = (ensemble: Set<number>, i: number): Set<number> => {
    const suivant = new Set(ensemble);
    if (suivant.has(i)) suivant.delete(i);
    else suivant.add(i);
    return suivant;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Importer depuis WhatsApp</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-muted-foreground text-sm">
          Dans WhatsApp, ouvrez le groupe → <em>Exporter la discussion</em> →{' '}
          <em>Sans les médias</em>, puis collez le texte ici. Rien ne sera enregistré avant que vous
          ayez regardé ce qui a été compris.
        </p>

        <div className="space-y-1">
          <Label htmlFor="wa_texte">Conversation</Label>
          <Textarea
            id="wa_texte"
            rows={6}
            value={texte}
            placeholder="[21/09/2026 17:08] Jo Toiture : Lundi 21 septembre Joaquim + Justin 7h 16h avec pause"
            onChange={(e) => {
              setTexte(e.target.value);
              setExclues(new Set());
              setDatesEcrites(new Set());
            }}
          />
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="wa_depuis">Du</Label>
            <Input
              id="wa_depuis"
              type="date"
              className="w-44"
              value={depuis}
              onChange={(e) => {
                setDepuis(e.target.value);
              }}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="wa_jusqua">Au</Label>
            <Input
              id="wa_jusqua"
              type="date"
              className="w-44"
              value={jusqua}
              onChange={(e) => {
                setJusqua(e.target.value);
              }}
            />
          </div>
        </div>

        {texte.trim() === '' ? null : (
          <>
            <p className="text-sm">
              <strong>{resultat.lignes.length}</strong> relevé
              {resultat.lignes.length > 1 ? 's' : ''} lu{resultat.lignes.length > 1 ? 's' : ''} ·{' '}
              {resultat.messagesSansHeures} message
              {resultat.messagesSansHeures > 1 ? 's' : ''} sans heures
            </p>

            {resultat.auteurs.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Qui poste pour quelle équipe ?</p>
                <div className="flex flex-wrap gap-3">
                  {resultat.auteurs.map((a) => (
                    <div key={a} className="space-y-1">
                      <Label htmlFor={`eq_${a}`} className="text-muted-foreground text-xs">
                        {a}
                      </Label>
                      <Input
                        id={`eq_${a}`}
                        className="w-40"
                        value={equipes[a] ?? a.split(/\s+/)[0] ?? a}
                        onChange={(e) => {
                          setEquipes({ ...equipes, [a]: e.target.value });
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {resultat.nomsNonReconnus.length > 0 ? (
              <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">
                  Mots croisés à côté d&apos;un nom connu, sans être reconnus
                </p>
                <p className="text-xs text-amber-800">
                  Il y a là des noms de chantiers — et peut-être un prénom écrit autrement, qui
                  serait sinon perdu. Rattachez ceux qui désignent quelqu&apos;un.
                </p>
                <div className="flex flex-wrap gap-2">
                  {resultat.nomsNonReconnus.map((n) => (
                    <span
                      key={n.mot}
                      className="bg-card flex items-center gap-1 rounded px-2 py-1 text-sm"
                    >
                      {n.mot}
                      <span className="text-muted-foreground text-xs">×{n.occurrences}</span>
                      <Select
                        aria-label={`Rattacher ${n.mot} à un salarié`}
                        className="h-8 w-36 text-xs"
                        value={aliasPour[n.mot] ?? ''}
                        onChange={(e) => {
                          setAliasPour({ ...aliasPour, [n.mot]: e.target.value });
                          if (e.target.value !== '') {
                            ajouterAlias.mutate({ employeId: e.target.value, alias: n.mot });
                          }
                        }}
                      >
                        <option value="">non, c&apos;est autre chose</option>
                        {employes.map((e) => (
                          <option key={e.id} value={e.id}>
                            c&apos;est {e.prenom}
                          </option>
                        ))}
                      </Select>
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {resultat.anomalies.length > 0 ? (
              <div className="border-destructive/30 bg-destructive/5 space-y-1 rounded-md border p-3">
                <p className="text-destructive flex items-center gap-2 text-sm font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  {resultat.anomalies.length} message
                  {resultat.anomalies.length > 1 ? 's' : ''} parle
                  {resultat.anomalies.length > 1 ? 'nt' : ''} d&apos;heures sans qu&apos;on ait su
                  les lire
                </p>
                <ul className="text-muted-foreground space-y-1 text-xs">
                  {resultat.anomalies.map((a, i) => (
                    <li key={`${a.code}-${String(i)}`}>
                      <span className="font-medium">{LIBELLE_ANOMALIE[a.code]}</span> — « {a.source}{' '}
                      »
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground text-xs">
                  Ces journées sont à saisir à la main : rien n&apos;a été deviné à leur place.
                </p>
              </div>
            ) : null}

            {resultat.lignes.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                      <th className="w-10 py-2">Garder</th>
                      <th className="py-2 pr-3">Jour</th>
                      <th className="py-2 pr-3">Équipe</th>
                      <th className="py-2 pr-3">Salarié</th>
                      <th className="py-2 pr-3">Horaires</th>
                      <th className="py-2 pr-3">Pause</th>
                      <th className="py-2 pr-3">Chantier</th>
                      <th className="py-2">À vérifier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultat.lignes.map((l, i) => {
                      const gardee = !exclues.has(i);
                      return (
                        <tr
                          key={`${l.jour}-${l.nom}-${String(i)}`}
                          className={`border-b ${gardee ? '' : 'opacity-40'}`}
                        >
                          <td className="py-1.5">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              aria-label={`Garder le relevé de ${l.nom} du ${l.jour}`}
                              checked={gardee}
                              onChange={() => {
                                setExclues(basculer(exclues, i));
                              }}
                            />
                          </td>
                          <td className="py-1.5 pr-3 tabular-nums">
                            {jourRetenu(l, i)}
                            {l.jourEcrit !== null ? (
                              <button
                                type="button"
                                className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-xs text-amber-800 hover:bg-amber-200"
                                onClick={() => {
                                  setDatesEcrites(basculer(datesEcrites, i));
                                }}
                              >
                                {datesEcrites.has(i) ? 'date d’envoi ?' : `→ ${l.jourEcrit}`}
                              </button>
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-3">{l.equipe}</td>
                          <td className="py-1.5 pr-3 font-medium">
                            {l.nom}
                            {l.employeId === null ? (
                              <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-xs text-amber-800">
                                à rattacher
                              </span>
                            ) : null}
                          </td>
                          <td className="py-1.5 pr-3 tabular-nums">
                            {formatMinutes(l.debut)} → {formatMinutes(l.fin)}
                          </td>
                          <td className="text-muted-foreground py-1.5 pr-3 tabular-nums">
                            {l.pauseDeclaree === null ? '—' : formatMinutes(l.pauseDeclaree)}
                          </td>
                          <td className="text-muted-foreground py-1.5 pr-3">
                            {l.chantierLibelle ?? '—'}
                          </td>
                          <td className="py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {l.anomalies.map((a) => (
                                <span
                                  key={a.code}
                                  title={a.message}
                                  className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                                >
                                  {LIBELLE_ANOMALIE[a.code]}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => {
                  enregistrer.mutate();
                }}
                disabled={retenues.length === 0 || enregistrer.isPending}
              >
                <Check className="mr-1.5 h-4 w-4" />
                {enregistrer.isPending
                  ? 'Enregistrement…'
                  : `Enregistrer ${String(retenues.length)} relevé${retenues.length > 1 ? 's' : ''}`}
              </Button>
              {enregistrer.isError ? (
                <span className="text-destructive text-sm">{messageErreur(enregistrer.error)}</span>
              ) : null}
              {ajouterAlias.isError ? (
                <span className="text-destructive text-sm">{messageErreur(ajouterAlias.error)}</span>
              ) : null}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
